/**
 * Comparator V2.5 product analytics → PostHog (custom events).
 *
 * Goes through the shared posthog-js SDK that `posthog.ts` initialises on
 * advisor calculator routes. The SDK also handles autocapture + session
 * recording (with `maskAllInputs`), so this module covers ONLY explicit
 * funnel/interaction events that complement autocapture.
 *
 * PII safety: every event's properties pass through a strict allowlist
 * (`SAFE_COMPARATOR_PROPERTIES`). Anything not on the list is dropped before
 * the request leaves the browser, so a client's name / e-mail / phone / PESEL
 * can never leak even if a call site passes it by mistake. Exact financial
 * figures (loan amount, period, LTV) are intentionally allowed — they are
 * simulation parameters, not client-identifying data. Identification happens
 * in `posthog.ts` (advisor id), so the distinct id is never the client.
 */
import posthog from 'posthog-js';
import { hasPosthogApiKey, ensureCalculatorUserIdentified } from '@/plugins/posthog';

export enum ComparatorEvent {
  // Funnel
  VIEWED = 'comparator_v25_viewed',
  SCHEDULE_GENERATED = 'comparator_v25_schedule_generated',
  SCHEDULE_SENT = 'comparator_v25_schedule_sent',
  ADDED_TO_DEAL = 'comparator_v25_offers_added_to_deal',
  APPLICATION_CREATED = 'comparator_v25_application_created',
  // Offer interactions
  OFFER_PINNED = 'comparator_v25_offer_pinned',
  OFFER_UNPINNED = 'comparator_v25_offer_unpinned',
  OFFER_SELECTED = 'comparator_v25_offer_selected',
  OFFER_UNSELECTED = 'comparator_v25_offer_unselected',
  OFFER_DETAILS_OPENED = 'comparator_v25_offer_details_opened',
  // Sorting / filtering / view
  SORT_CHANGED = 'comparator_v25_sort_changed',
  FILTER_CHANGED = 'comparator_v25_filter_changed',
  // Filter dead-end — active filters hid every offer while the pool was non-empty
  FILTERS_NO_RESULTS = 'comparator_v25_filters_no_results',
  VIEW_MODE_CHANGED = 'comparator_v25_view_mode_changed',
  // Real-estates tab ("Nieruchomości", shipped in #5065). `TAB_AVAILABLE` is the
  // denominator — it fires once per results view for every advisor who can SEE
  // the tab, so adoption is `TAB_OPENED` users / `TAB_AVAILABLE` users. Counting
  // `VIEW_MODE_CHANGED` alone cannot answer that: three different switchers
  // (results tabs, offers tabs, offers view switch) share that event name and
  // none of them reports the tab merely being on screen.
  REAL_ESTATES_TAB_AVAILABLE = 'comparator_v25_real_estates_tab_available',
  REAL_ESTATES_TAB_OPENED = 'comparator_v25_real_estates_tab_opened',
  REAL_ESTATES_TAB_CLOSED = 'comparator_v25_real_estates_tab_closed',
  // Thumbs up / down under the tab's placeholder — one reaction per browser
  // (see RealEstatesFeedbackThumbs.vue), so unique users ≈ number of opinions.
  REAL_ESTATES_TAB_REACTION = 'comparator_v25_real_estates_tab_reaction',
  // Optional free-text "why" submitted after the thumbs — a separate event so a
  // skipped comment never costs us the reaction itself.
  REAL_ESTATES_TAB_COMMENT = 'comparator_v25_real_estates_tab_comment',
  // Sharing / printout / send
  OFFERS_SHARED = 'comparator_v25_offers_shared',
  OFFERS_PRINTED = 'comparator_v25_offers_printed',
  // Intent — the "Send / Download" modal was opened (advisor has not yet sent)
  SEND_MODAL_OPENED = 'comparator_v25_send_modal_opened',
  // Success — the offer PDF was actually delivered to clients via the modal
  OFFERS_SENT = 'comparator_v25_offers_sent',
}

const ORIGIN_LIB = 'comparator-analytics';
const ORIGIN_LIB_VERSION = '1';

/**
 * The ONLY property keys allowed to leave the browser. Add keys here
 * deliberately — never widen this to "everything" and never add an
 * identity field (firstName, lastName, email, phone, pesel, address, …).
 */
// Key holding a per-bank breakdown — sanitiser special-cases it so the value
// reaches PostHog as a nested JSON object (queryable via HogQL) instead of
// being coerced to a string.
const SELECTED_BY_BANK_KEY = 'selectedByBank';

// Free-text key — the ONE deliberate exception to "no prose leaves the browser".
// Advisors can type anything here, a client's name included, so it is trimmed and
// hard-capped; empty comments are dropped instead of sent as ''. Keep this list at
// exactly one key: every further free-text field widens the PII surface.
const COMMENT_KEY = 'comment';
export const MAX_COMMENT_LENGTH = 500;

export const SAFE_COMPARATOR_PROPERTIES: ReadonlySet<string> = new Set([
  // Context / identifiers (advisor-side, opaque foreign key — not client PII)
  'simulationId',
  'productType',
  'loanPurpose',
  'source',
  // Counts
  'offersCount',
  'visibleOffersCount',
  'selectedOffersCount',
  'pinnedOffersCount',
  'activeFiltersCount',
  // Offer interaction
  'bankSymbol',
  'position',
  // Sorting / filtering / view
  'sortBy',
  'sortDirection',
  'filterCategory',
  'filterKey',
  'filterEnabled',
  'viewMode',
  // Real-estates tab engagement
  'dwellSeconds',
  'openCount',
  'reaction',
  COMMENT_KEY,
  'commentLength',
  // Sharing / printout / send
  'shareChannel',
  'format',
  // Selection-mix counters — describe the set of currently selected offers
  // at the moment of an action (sent/shared/added_to_deal/printed)
  SELECTED_BY_BANK_KEY,
  'selectedDistinctBanksCount',
  'selectedFixedRate',
  'selectedVariableRate',
  'selectedWithLifeInsurance',
  'selectedWithoutLifeInsurance',
  // Financial parameters of the simulation (exact values allowed by decision —
  // simulation params, not client identity)
  'loanAmount',
  'creditAmount',
  'propertyValue',
  'ownContribution',
  'loanPeriodInMonths',
  'ltv',
  'installment',
  'margin',
  'rrso',
]);

export type SelectedByBank = Readonly<Record<string, number>>;

type ComparatorPropertyValue = string | number | boolean | null | SelectedByBank;

const toSafePrimitiveValue = (value: unknown): string | number | boolean | null => {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (value === null || value === undefined) {
    return null;
  }
  return String(value);
};

// Accepts `{ PKO: 2, MBANK: 1 }` shape — keys must be non-empty strings, values
// finite numbers. Anything else in the object is dropped silently.
const sanitizeSelectedByBank = (value: unknown): SelectedByBank | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const safe: Record<string, number> = {};
  for (const [bank, count] of Object.entries(value as Record<string, unknown>)) {
    if (typeof bank === 'string' && bank.length > 0 && typeof count === 'number' && Number.isFinite(count)) {
      safe[bank] = count;
    }
  }
  return safe;
};

export const sanitizeComparatorProperties = (
  properties: Record<string, unknown>,
): Record<string, ComparatorPropertyValue> => {
  const safeProperties: Record<string, ComparatorPropertyValue> = {};

  Object.entries(properties).forEach(([key, value]) => {
    if (!SAFE_COMPARATOR_PROPERTIES.has(key)) {
      return;
    }
    if (key === SELECTED_BY_BANK_KEY) {
      const sanitized = sanitizeSelectedByBank(value);
      if (sanitized) safeProperties[key] = sanitized;
      return;
    }
    if (key === COMMENT_KEY) {
      const comment = typeof value === 'string' ? value.trim().slice(0, MAX_COMMENT_LENGTH) : '';
      if (comment) safeProperties[key] = comment;
      return;
    }
    safeProperties[key] = toSafePrimitiveValue(value);
  });

  return safeProperties;
};

/**
 * Send a Comparator V2.5 analytics event. Properties are sanitized through
 * the allowlist; `$origin_lib` tags the source so events are filterable in
 * PostHog. Always resolves — never throws. Prefer calling via the
 * `useComparatorAnalytics` composable, which scopes sends to V2.5 and injects
 * simulationId/productType automatically.
 */
export const captureComparatorEvent = (
  event: ComparatorEvent,
  rawProperties: Record<string, unknown> = {},
): void => {
  if (!hasPosthogApiKey()) {
    return;
  }

  // Catches the cold-reload race where AuthService.User landed AFTER the
  // route-driven identify in posthog.ts. Idempotent.
  ensureCalculatorUserIdentified();

  const properties: Record<string, unknown> = {
    ...sanitizeComparatorProperties(rawProperties),
    $origin_lib: ORIGIN_LIB,
    $origin_lib_version: ORIGIN_LIB_VERSION,
  };

  posthog.capture(event, properties);
};
